import { computed, ref, Ref } from "vue";
import { financesStore } from "@/store/finances";
import {
	BillInfo,
	BillConnectionToProjectWithValues,
	BillConnectionToItemIncomeWithValues,
	ProjectInBillConnection,
	StorageItemMovementInFinances
} from "@/models/interfaces/finances/finance-models";
import { BillConnectionWithValuesEditAsker } from "@/models/interfaces/storage/storage-models";
import { BillConnectionWithValuesTypeDescriptor } from "@/models/etc/types";

// Локальная редактируемая view-model привязки (интерфейс BillConnectionWithValuesBase «плоский», без методов —
// поэтому состояние правки (Text/Inner/Valid) держим здесь).
export interface EditableConnection {
	Id: number;
	Descriptor: BillConnectionWithValuesTypeDescriptor;
	project?: ProjectInBillConnection;
	income?: StorageItemMovementInFinances;

	AbsoluteAmountText: string;
	AmountPercentText: string;
	PayedAmountText: string;

	AbsoluteAmountInner: number;
	AmountPercentInner: number;
	PayedAmountInner: number;

	AbsoluteAmountValid: boolean;
	AmountPercentValid: boolean;
	PayedAmountValid: boolean;

	PayedPercent: number;
	NotSavedChanges: boolean;
	amountEdited: boolean;
}

// округление до N знаков через экспоненциальную запись
export const roundTo = (num: number, places: number): number =>
	Number(`${Math.round(Number(`${num}e${places}`))}e-${places}`)

const parseNum = (t: string): number => Number(String(t).replace(',', '.'))

export function useBillConnectionsEditor(info: Ref<BillInfo | null>) {
	const rows = ref<EditableConnection[]>([])
	const dirty = ref(false)
	const errorText = ref('')

	// агрегированные метки ошибок (подсветка полей)
	const percentErr = ref(false)
	const amountErr = ref(false)
	const debitErr = ref(false)

	const billCalculatedAmount = computed(() => info.value?.CalculatedAmount ?? 0)
	const billPayedAmount = computed(() => info.value?.PayedAmount ?? 0)
	const totalDebits = computed(() => rows.value.reduce((s, r) => s + (r.PayedAmountInner || 0), 0))

	const makeRow = (conn: BillConnectionToProjectWithValues | BillConnectionToItemIncomeWithValues): EditableConnection => ({
		Id: conn.Id,
		Descriptor: conn.Descriptor,
		project: (conn as BillConnectionToProjectWithValues).Project,
		income: (conn as BillConnectionToItemIncomeWithValues).Income,
		AbsoluteAmountInner: conn.AbsoluteAmount ?? 0,
		AmountPercentInner: conn.AmountPercent ?? 0,
		PayedAmountInner: conn.PayedAmount ?? 0,
		AbsoluteAmountText: String(conn.AbsoluteAmount ?? 0),
		AmountPercentText: String(conn.AmountPercent ?? 0),
		PayedAmountText: String(conn.PayedAmount ?? 0),
		AbsoluteAmountValid: true,
		AmountPercentValid: true,
		PayedAmountValid: true,
		PayedPercent: conn.PayedPercent ?? 0,
		NotSavedChanges: false,
		amountEdited: false,
	})

	const init = () => {
		rows.value = (info.value?.Connections ?? []).map(
			c => makeRow(c as BillConnectionToProjectWithValues | BillConnectionToItemIncomeWithValues)
		)
		dirty.value = false
		errorText.value = ''
		percentErr.value = amountErr.value = debitErr.value = false
	}

	const recalcPayedPercent = (row: EditableConnection) => {
		row.PayedPercent = row.AbsoluteAmountInner > 0
			? roundTo((row.PayedAmountInner / row.AbsoluteAmountInner) * 100, 0)
			: 0
	}

	// type: 0 = %, 1 = сумма, 2 = дебет
	const onFieldInput = (row: EditableConnection, type: number, value: string) => {
		const calc = billCalculatedAmount.value
		if (type === 0) {
			row.AmountPercentText = value
			const v = parseNum(value)
			row.AmountPercentValid = value !== '' && !Number.isNaN(v) && v >= 0 && v <= 100
			row.AmountPercentInner = row.AmountPercentValid ? v : 0
			row.AbsoluteAmountInner = roundTo((calc * row.AmountPercentInner) / 100, 2)
			row.AbsoluteAmountText = String(row.AbsoluteAmountInner)
			row.AbsoluteAmountValid = true
			row.amountEdited = true
		} else if (type === 1) {
			row.AbsoluteAmountText = value
			const v = parseNum(value)
			row.AbsoluteAmountValid = value !== '' && !Number.isNaN(v) && v >= 0
			row.AbsoluteAmountInner = row.AbsoluteAmountValid ? v : 0
			row.AmountPercentInner = calc ? roundTo((row.AbsoluteAmountInner / calc) * 100, 2) : 0
			row.AmountPercentText = String(row.AmountPercentInner)
			row.AmountPercentValid = true
			row.amountEdited = true
		} else {
			row.PayedAmountText = value
			const v = parseNum(value)
			row.PayedAmountInner = Number.isNaN(v) ? 0 : v
			row.PayedAmountValid = value !== '' && !Number.isNaN(v) && v >= 0 && v <= row.AbsoluteAmountInner
		}
		recalcPayedPercent(row)
		row.NotSavedChanges = true
		dirty.value = true
		checkChangesPresent()
	}

	// на blur — зажимаем дебет по образцу Angular checkDebit
	const onFieldBlur = (row: EditableConnection) => {
		if (row.AbsoluteAmountInner < row.PayedAmountInner) {
			row.PayedAmountInner = row.AbsoluteAmountInner
			row.PayedAmountText = String(row.PayedAmountInner)
		} else if (row.amountEdited) {
			const otherDebits = totalDebits.value - row.PayedAmountInner
			const remainder = roundTo(billPayedAmount.value - otherDebits, 2)
			const cap = remainder < 0 ? 0 : remainder
			row.PayedAmountInner = Math.min(row.AbsoluteAmountInner, cap)
			row.PayedAmountText = String(row.PayedAmountInner)
		}
		row.PayedAmountValid = row.PayedAmountInner >= 0 && row.PayedAmountInner <= row.AbsoluteAmountInner
		recalcPayedPercent(row)
		checkChangesPresent()
	}

	// живые метки ошибок (raw Inner + допуск +0.1)
	const checkChangesPresent = () => {
		percentErr.value = amountErr.value = debitErr.value = false
		errorText.value = ''
		let sp = 0, sv = 0, sd = 0
		for (const r of rows.value) {
			sp += r.AmountPercentInner
			sv += r.AbsoluteAmountInner
			sd += r.PayedAmountInner
		}
		if (sp < 0 || sp > 100) { percentErr.value = true; errorText.value = '% не сходятся'; return }
		if (sv < 0 || sv > billCalculatedAmount.value + 0.1) { amountErr.value = true; errorText.value = 'Суммы не сходятся'; return }
		if (sd > billPayedAmount.value + 0.1) { debitErr.value = true; errorText.value = 'Дебет не сходится'; return }
	}

	const detach = (row: EditableConnection) => {
		rows.value = rows.value.filter(r => !(r.Id === row.Id && r.Descriptor === row.Descriptor))
		dirty.value = true
		checkChangesPresent()
	}

	// отправка (строгое округление до 2 знаков, без допуска) + коррекция дрейфа
	const save = async (billId: number): Promise<boolean> => {
		const calc = billCalculatedAmount.value
		const payed = billPayedAmount.value
		const newValues = rows.value.map(r => ({
			Id: r.Id,
			Descriptor: r.Descriptor,
			value: roundTo(r.AbsoluteAmountInner, 2),
			value_p: roundTo(r.AmountPercentInner, 2),
			debit: roundTo(r.PayedAmountInner, 2),
		}))

		let sp = 0, sv = 0, sd = 0
		for (const r of rows.value) {
			sp = roundTo(sp + roundTo(r.AmountPercentInner, 2), 2)
			sv = roundTo(sv + roundTo(r.AbsoluteAmountInner, 2), 2)
			sd = roundTo(sd + roundTo(r.PayedAmountInner, 2), 2)
		}

		if (sp < 0 || sp > 100) { errorText.value = 'Сумма привязок должна быть от 0 до 100%'; return false }
		if (sv < 0 || sv > calc) { errorText.value = 'Сумма привязок должна быть от 0 до суммы счёта'; return false }
		if (sd > payed) { errorText.value = 'Сумма привязок не может превышать сумму оплаты по счёту'; return false }

		const diffValues = calc - sv
		const diffPercent = 100 - sp
		if (sp === 100 && diffValues !== 0) {
			const i = newValues.findIndex(v => v.value + diffValues >= 0)
			if (i >= 0) newValues[i].value += diffValues
		} else if (sv === calc && diffPercent !== 0) {
			const i = newValues.findIndex(v => v.value_p + diffPercent >= 0)
			if (i >= 0) newValues[i].value_p += diffPercent
		}

		const asker: BillConnectionWithValuesEditAsker = {
			BillId: billId,
			Connections: newValues.map(c => ({
				Id: c.Id,
				Descriptor: c.Descriptor,
				AbsoluteAmount: c.value,
				AmountPercent: c.value_p,
				PayedAmount: c.debit,
			})),
		}

		const res = await financesStore().updateConnectionValues(asker)
		if (!res.response_ok) { errorText.value = 'Не удалось сохранить привязки'; return false }
		return true
	}

	return {
		rows,
		dirty,
		errorText,
		percentErr,
		amountErr,
		debitErr,
		billCalculatedAmount,
		init,
		onFieldInput,
		onFieldBlur,
		detach,
		save,
	}
}
